/**
 * 難易度別のガイド表示制御(docs/02_game_design.md §2)。
 *
 * 判定に使う値はすべて engine の `DIFFICULTY_SETTINGS` から読む。
 * ここに数値を書かないこと(CLAUDE.md ルール3)。
 *
 * 日本語モード:
 * |                | かんたん | ふつう | むずかしい |
 * |---|---|---|---|
 * | キーボード+指ガイド | 常時 | 出題直後だけ。最初の入力で消え、`hintIdleMs` 無操作で再表示 | 非表示 |
 * | ローマ字ガイド | 常時全文 | 出題後 `romajiPreviewMs` だけ全文→以降は打った分のみ(無操作で再表示) | 打った分のみ |
 *
 * 英語モード(綴り = そのままタイプする文字列なので、扱いを分けている):
 * | かんたん | 常時、タイプ文字列+キーボードを表示 |
 * | ふつう | 訳+音声だけで挑戦。`englishAssistIdleMs` 無操作 か ミスタイプで
 *            正解のタイプ文字列+キーボードを出す(その語のあいだは出したまま) |
 * | むずかしい | 表示補助なし |
 */

import type { DifficultySettings } from '@paltyping/engine';
import type { Language } from '../services/content';

export interface GuideInput {
  readonly settings: DifficultySettings;
  /** 出題(wordStart)からの経過時間(ms)。 */
  readonly wordElapsedMs: number;
  /** 直近の受理打鍵からの経過時間(ms)。 */
  readonly idleMs: number;
  /** 現在の語で1打以上入力したか。 */
  readonly hasTypedInWord: boolean;
  /** 出題モード。既定は日本語(従来どおりの挙動)。 */
  readonly language?: Language;
  /**
   * 現在の語で観測した最長の無操作時間(ms)。省略時は `idleMs`。
   * 英語ふつうの助け舟は「いちど出したら引っ込めない」ので、最大値で判定する。
   */
  readonly maxIdleMs?: number;
  /** 現在の語でミスタイプしたか。 */
  readonly missedInWord?: boolean;
}

export interface GuideVisibility {
  /** 'full' = 残りも見せる / 'typedOnly' = 打った分だけ。 */
  readonly romaji: 'full' | 'typedOnly';
  /** キーボード+指ガイドを出すか。 */
  readonly keyboard: boolean;
  /** ヒントとして再表示している状態か(演出を変えるため)。 */
  readonly hintActive: boolean;
}

/** 「入力が止まったのでヒントを出す」状態か。 */
export function isHintActive(input: GuideInput): boolean {
  const { hintIdleMs } = input.settings;
  if (hintIdleMs <= 0) return false;
  if (!input.hasTypedInWord) return false;
  return input.idleMs >= hintIdleMs;
}

export interface EnglishAssistInput {
  readonly settings: DifficultySettings;
  /** 現在の語で観測した最長の無操作時間(ms)。 */
  readonly maxIdleMs: number;
  /** 現在の語でミスタイプしたか。 */
  readonly missedInWord: boolean;
}

/**
 * 英語モードで「正解のタイプ文字列 + キーボードガイド」を出すか。
 *
 * - かんたん(`showEnglishWord`): 常に出す
 * - むずかしい(`englishAssistIdleMs === 0`): 出さない
 * - ふつう: 一定時間 手が止まった / ミスタイプした ら出す
 */
export function resolveEnglishAssist(input: EnglishAssistInput): boolean {
  const { showEnglishWord, englishAssistIdleMs } = input.settings;
  if (showEnglishWord) return true;
  if (englishAssistIdleMs <= 0) return false;
  return input.missedInWord || input.maxIdleMs >= englishAssistIdleMs;
}

/** ローマ字ガイド・キーボードガイドの表示状態を解決する。 */
export function resolveGuideVisibility(input: GuideInput): GuideVisibility {
  const { settings } = input;

  if (input.language === 'english') {
    const assisted = resolveEnglishAssist({
      settings,
      maxIdleMs: input.maxIdleMs ?? input.idleMs,
      missedInWord: input.missedInWord ?? false,
    });
    if (settings.showEnglishWord) {
      return { romaji: 'full', keyboard: settings.keyboardGuide !== 'hidden', hintActive: false };
    }
    // 助け舟は「出す/出さない」だけ。点滅させると打っている最中に読みづらい。
    return { romaji: assisted ? 'full' : 'typedOnly', keyboard: assisted, hintActive: false };
  }

  const hintActive = isHintActive(input);

  let romaji: 'full' | 'typedOnly';
  switch (settings.romajiGuide) {
    case 'full':
      romaji = 'full';
      break;
    case 'preview':
      // 出題直後のプレビュー中、またはヒント再表示中は全文。
      romaji = input.wordElapsedMs < settings.romajiPreviewMs || hintActive ? 'full' : 'typedOnly';
      break;
    case 'typedOnly':
    default:
      romaji = 'typedOnly';
      break;
  }

  let keyboard: boolean;
  switch (settings.keyboardGuide) {
    case 'always':
      keyboard = true;
      break;
    case 'untilFirstInput':
      keyboard = !input.hasTypedInWord || hintActive;
      break;
    case 'hidden':
    default:
      keyboard = false;
      break;
  }

  return { romaji, keyboard, hintActive };
}

export interface WordPresentation {
  /** 大きな問題文(漢字混じりの日本語)を出すか。英語は出さない(タイプ文字列だけ)。 */
  readonly showWordText: boolean;
  /**
   * 「○○ を えいごで!」の飾り付きで訳を出すか(英語 ふつう/むずかしい)。
   * 綴りを伏せた状態で挑戦させるための手がかり。
   */
  readonly showMeaningPrompt: boolean;
  /**
   * 飾り無しで訳だけを出すか(英語 かんたん)。
   * 位置・デザインは `showMeaningPrompt` と同じ。
   */
  readonly showMeaningPlain: boolean;
}

/**
 * 問題文の出し方(docs/02 §2「英語モードの単語表示」)。
 *
 * 英語モードは「元の単語(display)」と「タイプする文字列」が同じなので、
 * 大きな問題文は出さず、タイプする文字列(RomajiGuide)だけを見せる。
 */
export function resolveWordPresentation(args: {
  readonly settings: DifficultySettings;
  readonly language: Language;
}): WordPresentation {
  if (args.language === 'japanese') {
    return { showWordText: true, showMeaningPrompt: false, showMeaningPlain: false };
  }
  const easy = args.settings.showEnglishWord;
  return {
    showWordText: false,
    showMeaningPrompt: !easy,
    showMeaningPlain: easy,
  };
}
